// Phase 6.1 — create-user modal (superadmin).
//
// Two-step flow:
//   1. This modal collects username / email / initial password /
//      role and validates client-side (same rules the server
//      enforces, so the common case never round-trips a 4xx).
//   2. On Create it opens <StepUpAuthModal>, which asks the
//      superadmin for THEIR current password and runs the actual
//      POST /users with it.
//
// Phase 6.3.2 (A.2): chrome is now the <Modal> primitive — a REAL
// modal with a backdrop that blocks the table behind it (the 6.1
// version was a backdrop-less card). The nested StepUpAuthModal is
// also a Modal; the primitive's modal-stack makes Esc only close
// the topmost. A dirty form asks "Discard changes?" before closing.
//
// Error mapping back onto the form fields:
//   * 401 from step-up → handled INSIDE StepUpAuthModal.
//   * 409 / 422 / 400  → field-shaped errors mapped onto the form;
//     the step-up modal closes and the offending field shows it.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

import {
  ApiError,
  createUser,
  type CreateUserResponse,
} from "../lib/api";
import { StepUpAuthModal } from "./StepUpAuthModal";
import { Modal } from "./ui/Modal";

const USERNAME_RE = /^[a-z0-9_-]{3,32}$/;
const EMAIL_RE = /^[a-z0-9._+\-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/;
const USERNAME_HINT =
  "3–32 chars: lowercase letters, digits, hyphen, underscore.";
const EMAIL_HINT = "Used for Slack workspace attribution.";
const PASSWORD_HINT = "At least 8 characters.";

type Role = "user" | "superadmin";

interface FieldErrors {
  username?: string;
  email?: string;
  password?: string;
  role?: string;
}

function validate(username: string, email: string, password: string): FieldErrors {
  const out: FieldErrors = {};
  const u = username.trim().toLowerCase();
  if (u && !USERNAME_RE.test(u)) out.username = USERNAME_HINT;
  const e = email.trim().toLowerCase();
  if (e && !EMAIL_RE.test(e)) out.email = "Enter a valid email address.";
  if (password && password.length < 8) out.password = PASSWORD_HINT;
  return out;
}

/** Map a server 4xx body onto specific form fields. Returns the
 *  field errors plus an optional non-field message. */
function mapServerErrors(err: ApiError): {
  fields: FieldErrors;
  message: string | null;
} {
  const fields: FieldErrors = {};
  const body = err.responseBody as { detail?: unknown } | null | undefined;
  const detail = body?.detail;

  if (Array.isArray(detail)) {
    for (const item of detail) {
      const loc = (item as { loc?: unknown }).loc;
      const msg = (item as { msg?: unknown }).msg;
      if (Array.isArray(loc) && typeof msg === "string") {
        const field = String(loc[loc.length - 1]);
        if (field === "username") fields.username = msg;
        else if (field === "email") fields.email = msg;
        else if (field === "password") fields.password = msg;
        else if (field === "role") fields.role = msg;
      }
    }
    if (Object.keys(fields).length > 0) return { fields, message: null };
  }

  if (typeof detail === "string") {
    const lower = detail.toLowerCase();
    if (lower.includes("username")) {
      fields.username = detail;
      return { fields, message: null };
    }
    if (lower.includes("email")) {
      fields.email = detail;
      return { fields, message: null };
    }
    if (lower.includes("role")) {
      fields.role = detail;
      return { fields, message: null };
    }
    return { fields, message: detail };
  }

  return { fields, message: err.message };
}

/** The form fields + step-up password handed to the submit fn. */
export interface CreateUserBody {
  username: string;
  email: string;
  password: string;
  role: Role;
  current_password: string;
}

interface AddUserModalProps {
  /** Called after a successful create. Parent should refresh the
   *  list and unmount this modal. */
  onCreated: (user: CreateUserResponse) => void;
  /** Cancel / Esc / backdrop. Parent unmounts the modal. */
  onClose: () => void;
  /** Phase 6.0b: pre-fill the email field. Used by the pending-
   *  workspace queue's "Create account" affordance so the operator
   *  doesn't re-type the Slack-side email that needs an account.
   *  Still editable. */
  defaultEmail?: string;
  /** Modal heading. Default "Add user". */
  title?: string;
  /** Phase 6.0b: override the submit call. Default is POST /users
   *  (createUser). The pending-workspace queue passes a fn that
   *  POSTs /pending-workspaces/{id}/create-and-attribute so the
   *  account is created AND the workspace bound in one step-up. The
   *  form validation / error mapping / step-up chrome are reused
   *  verbatim. */
  submitOverride?: (body: CreateUserBody) => Promise<CreateUserResponse>;
  /** Override the step-up modal's body copy (e.g. to note the
   *  workspace will also be activated). */
  stepUpDescription?: React.ReactNode;
}

export function AddUserModal({
  onCreated,
  onClose,
  defaultEmail,
  title = "Add user",
  submitOverride,
  stepUpDescription,
}: AddUserModalProps) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [showPassword, setShowPassword] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [showStepUp, setShowStepUp] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const usernameRef = useRef<HTMLInputElement>(null);

  const clientErrors = useMemo(
    () => validate(username, email, password),
    [username, email, password],
  );

  const canSubmit =
    username.trim().length >= 3 &&
    email.trim().length > 0 &&
    password.length >= 8 &&
    Object.keys(clientErrors).length === 0;

  const errorFor = (f: keyof FieldErrors): string | undefined =>
    serverErrors[f] ?? clientErrors[f];

  // A pre-filled email isn't "dirty" on its own — only count edits
  // beyond the provided default so the discard guard doesn't fire on
  // an untouched pre-filled form.
  const isDirty =
    username.length > 0 ||
    email !== (defaultEmail ?? "") ||
    password.length > 0 ||
    role !== "user";

  // Claim focus for the username field (Modal focuses its X first).
  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  // A.2: a dirty form asks before discarding. Routed through Modal's
  // onClose (Esc / backdrop / X) and the Cancel button.
  const requestClose = useCallback(() => {
    if (isDirty) {
      setConfirmDiscard(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const editUsername = (v: string) => {
    setUsername(v);
    if (serverErrors.username) setServerErrors((s) => ({ ...s, username: undefined }));
  };
  const editEmail = (v: string) => {
    setEmail(v);
    if (serverErrors.email) setServerErrors((s) => ({ ...s, email: undefined }));
  };
  const editPassword = (v: string) => {
    setPassword(v);
    if (serverErrors.password) setServerErrors((s) => ({ ...s, password: undefined }));
  };

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    setGlobalError(null);
    setServerErrors({});
    setShowStepUp(true);
  };

  const doCreate = useCallback(
    async (currentPassword: string) => {
      try {
        const submit = submitOverride ?? createUser;
        const created = await submit({
          username: username.trim().toLowerCase(),
          email: email.trim().toLowerCase(),
          password,
          role,
          current_password: currentPassword,
        });
        setShowStepUp(false);
        onCreated(created);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          throw err; // wrong step-up password — StepUpAuthModal owns it
        }
        if (
          err instanceof ApiError &&
          (err.status === 409 || err.status === 422 || err.status === 400)
        ) {
          const { fields, message } = mapServerErrors(err);
          setServerErrors(fields);
          setGlobalError(message);
          setShowStepUp(false);
          return;
        }
        throw err; // network / unexpected — surface in the step-up modal
      }
    },
    [username, email, password, role, onCreated, submitOverride],
  );

  return (
    <Modal open onClose={requestClose} title={title} size="md">
      <form onSubmit={onFormSubmit} className="space-y-loose" data-testid="add-user-form">
        <Field
          id="add-user-username"
          label="Username"
          value={username}
          onChange={editUsername}
          autoComplete="off"
          error={errorFor("username")}
          hint={USERNAME_HINT}
          inputRef={usernameRef}
        />
        <Field
          id="add-user-email"
          type="email"
          label="Email"
          value={email}
          onChange={editEmail}
          autoComplete="off"
          error={errorFor("email")}
          hint={EMAIL_HINT}
        />
        <div>
          <label htmlFor="add-user-password" className="block text-caption mb-1.5">
            Initial password
          </label>
          <div className="relative">
            <input
              id="add-user-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => editPassword(e.target.value)}
              autoComplete="new-password"
              data-testid="add-user-password"
              aria-invalid={errorFor("password") ? "true" : "false"}
              aria-describedby={
                errorFor("password") ? "add-user-password-error" : "add-user-password-hint"
              }
              className={[
                "w-full px-3 py-2 pr-10 bg-theme-bg-app border font-mono text-sm",
                "text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none",
                errorFor("password")
                  ? "border-theme-error-border focus:border-theme-error-border"
                  : "border-theme-border-default focus:border-theme-border-active",
              ].join(" ")}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              data-testid="add-user-password-toggle"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 px-3 flex items-center text-theme-text-tertiary hover:text-theme-text-primary transition-colors duration-150"
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" strokeWidth={1.5} aria-hidden />
              ) : (
                <Eye className="w-4 h-4" strokeWidth={1.5} aria-hidden />
              )}
            </button>
          </div>
          {errorFor("password") ? (
            <p id="add-user-password-error" data-testid="add-user-password-error" className="mt-1 text-caption text-theme-error-text">
              {errorFor("password")}
            </p>
          ) : (
            <p id="add-user-password-hint" className="mt-1 text-caption">
              {PASSWORD_HINT}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="add-user-role" className="block text-caption mb-1.5">
            Role
          </label>
          <select
            id="add-user-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            data-testid="add-user-role"
            className="w-full px-3 py-2 bg-theme-bg-app border border-theme-border-default font-mono text-sm text-theme-text-primary focus:outline-none focus:border-theme-border-active"
          >
            <option value="user">user</option>
            <option value="superadmin">superadmin</option>
          </select>
          {errorFor("role") && (
            <p data-testid="add-user-role-error" className="mt-1 text-caption text-theme-error-text">
              {errorFor("role")}
            </p>
          )}
        </div>

        {globalError && (
          <p
            data-testid="add-user-global-error"
            role="alert"
            className="text-body-sm text-theme-error-text bg-theme-error-bg border border-theme-error-border px-3 py-2 leading-relaxed"
          >
            {globalError}
          </p>
        )}

        <div className="flex justify-end gap-snug pt-1">
          <button
            type="button"
            onClick={requestClose}
            data-testid="add-user-cancel"
            className="px-3 py-1.5 border border-theme-border-default text-caption text-theme-text-secondary hover:text-theme-text-primary hover:border-theme-border-emphasis transition-colors duration-150"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="add-user-submit"
            className="inline-flex items-center gap-tight px-3 py-1.5 border border-theme-border-default text-caption text-theme-text-primary hover:border-theme-text-primary transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-theme-border-default"
          >
            Create
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      </form>

      {/* A.2: discard guard for a dirty form. Inline (not a 3rd
          modal layer) — Keep editing / Discard. */}
      {confirmDiscard && (
        <div
          data-testid="add-user-discard-confirm"
          className="mt-loose border-t border-theme-border-default pt-default flex items-center justify-between gap-default"
        >
          <span className="text-body-sm">Discard changes?</span>
          <div className="flex gap-snug">
            <button
              type="button"
              onClick={() => setConfirmDiscard(false)}
              data-testid="add-user-discard-keep"
              className="px-3 py-1.5 border border-theme-border-default text-caption text-theme-text-secondary hover:text-theme-text-primary transition-colors duration-150"
            >
              Keep editing
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="add-user-discard-confirm-btn"
              className="px-3 py-1.5 border border-theme-error-border text-caption text-theme-error-text hover:bg-theme-error-bg transition-colors duration-150"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {showStepUp && (
        <StepUpAuthModal
          title="Confirm with your password"
          description={
            stepUpDescription ?? (
              <>
                You're creating user{" "}
                <span className="text-theme-text-primary">
                  {username.trim().toLowerCase()}
                </span>
                . Enter your own password to authorize.
              </>
            )
          }
          confirmLabel="Create user"
          onConfirm={doCreate}
          onClose={() => setShowStepUp(false)}
        />
      )}
    </Modal>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  error?: string;
  hint?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}

function Field({
  id,
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
  error,
  hint,
  inputRef,
}: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-caption mb-1.5">
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        data-testid={id}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={[
          "w-full px-3 py-2 bg-theme-bg-app border font-mono text-sm",
          "text-theme-text-primary placeholder-theme-text-tertiary focus:outline-none",
          error
            ? "border-theme-error-border focus:border-theme-error-border"
            : "border-theme-border-default focus:border-theme-border-active",
        ].join(" ")}
      />
      {error ? (
        <p id={`${id}-error`} data-testid={`${id}-error`} className="mt-1 text-caption text-theme-error-text">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-caption">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
